import { IDocument } from '@query'
import { PermissionSet, useBusinessContext, useFlag, useFlagsLoaded, usePermission } from '@context'
import { getWorkflowBuilderPageUrl } from '@constants'
import styled from 'styled-components'
import { LuClock12 } from 'react-icons/lu'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

interface Props {
  document: IDocument
}

export const AlgorithmStatusBadge: React.FC<Props> = ({ document }) => {
  const { t } = useTranslation()
  const { slug } = useBusinessContext()
  const isAdmin = usePermission(PermissionSet.ADMIN)
  const flagsLoaded = useFlagsLoaded()
  const workflowsEnabled = useFlag('workflows').enabled

  const pendingRuns = document?.import_data?.pending_workflow_runs ?? []
  const primaryRun = pendingRuns[0]

  if (primaryRun) {
    const canOpenRun =
      flagsLoaded &&
      workflowsEnabled &&
      isAdmin &&
      Number.isFinite(primaryRun.workflow_id) &&
      primaryRun.workflow_id > 0

    return (
      <BaseBadge className={'running'}>
        <AnimatedClock>
          <LuClock12 className="hours" />
          <LuClock12 className="minutes" />
        </AnimatedClock>
        <span>{t('document.algoWaitingForAutomation')}</span>
        {canOpenRun && (
          <RunLink
            to={getWorkflowBuilderPageUrl(slug, primaryRun.workflow_id, {
              tab: 'runs',
              run: primaryRun.id
            })}
          >
            {t('document.algoWaitingForAutomationLink')}
          </RunLink>
        )}
      </BaseBadge>
    )
  }

  if (document?.import_data?.algo_status == 'RUNNING') {
    return (
      <BaseBadge className={'running'}>
        <AnimatedClock>
          <LuClock12 className="hours" />
          <LuClock12 className="minutes" />
        </AnimatedClock>
        <span>{t('document.algoRunning')}</span>
      </BaseBadge>
    )
  }

  return null
}

const BaseBadge = styled.div`
  width: fit-content;
  color: ${({ theme }) => theme.colors.metalGray};
  margin-top: ${({ theme }) => theme.spacing.xxs}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  border-radius: 0.4rem;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 500;

  &.running {
    color: ${({ theme }) => theme.colors.metalGray};

    & svg {
      color: ${({ theme }) => theme.colors.metalGray};
    }
  }
`

const RunLink = styled(Link)`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const AnimatedClock = styled.div`
  position: relative;
  width: ${({ theme }) => theme.fontSize.sm}rem;
  height: ${({ theme }) => theme.fontSize.sm}rem;

  & > * {
    color: ${({ theme }) => theme.colors.metalGray};
    font-size: ${({ theme }) => theme.fontSize.sm}rem;
    position: absolute;
    top: 50%;
    left: 50%;
  }

  .hours {
    animation: rotate 2s infinite linear;
  }

  .minutes {
    animation: rotate 24s infinite linear;
  }

  @keyframes rotate {
    from {
      transform: translate(-50%, -50%) rotate(0deg);
    }
    to {
      transform: translate(-50%, -50%) rotate(360deg);
    }
  }
`
